import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { reportsApi } from "../../components/manageData/reportsApi";
import { formatISODateIST } from "../../utils/istDateUtils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, isValid } from "date-fns";
import { getUserDateFormat, convertToDateFnsFormat } from "../../utils/formatDate";
import useAnalyticsSensorCharts from "./AnalyticsSensorCharts";
import {
  createAnalyticsDateChunks,
  mergeReportResponses,
  validateAnalyticsDateRange,
} from "../../utils/analyticsDateRange";

const axisStyle = {
  fontSize: 12,
  fill: "#5F6B7A",
};

const denseAxisStyle = {
  ...axisStyle,
  fontSize: 10,
};

const getXAxisProps = (data = []) => ({
  dataKey: "time",
  tick: data.length > 12 ? denseAxisStyle : axisStyle,
  interval: data.length > 14 ? Math.ceil(data.length / 8) : 0,
  minTickGap: 18,
});

const chartCardSx = {
  border: "1px solid #A5DCF3",
  borderRadius: "8px",
  backgroundColor: "#FFFFFF",
  boxShadow: "0px 2px 5px rgba(0,0,0,0.16)",
  p: 2,
  minHeight: 340,
  display: "flex",
  flexDirection: "column",
};

const chartFrameSx = {
  mt: 2,
  flex: 1,
  minHeight: 260,
  borderRadius: "8px",
  backgroundColor: "#F8F9FB",
  p: 1.5,
};

const tripStatusColors = {
  Delayed: "#FFE600",
  Missed: "#F33A2F",
  "On Time": "#28A745",
};

const getOrganizationId = () => {
  try {
    const userState = JSON.parse(localStorage.getItem("userState"));
    return userState?.user?.organizationId || "";
  } catch (_error) {
    return "";
  }
};

const getRows = (report) => (Array.isArray(report?.data) ? report.data : []);

const flattenHaltRows = (report) => {
  if (Array.isArray(report?.data) && report.data.length) return report.data;

  if (!Array.isArray(report?.devices)) return [];

  return report.devices.flatMap((device) => {
    const inactivePeriods = Array.isArray(device?.inactivePeriods)
      ? device.inactivePeriods
      : [];
    const deadPeriods = Array.isArray(device?.deadPeriods)
      ? device.deadPeriods
      : [];

    return [
      ...inactivePeriods.map((period) => ({
        ...period,
        type: period?.type || period?.haltType || "inactive",
        vehicleName: device?.vehicleName,
        vehicleNumber: device?.vehicleNumber || device?.deviceId,
      })),
      ...deadPeriods.map((period) => ({
        ...period,
        type: period?.type || period?.haltType || "dead",
        vehicleName: device?.vehicleName,
        vehicleNumber: device?.vehicleNumber || device?.deviceId,
      })),
    ];
  });
};

const getTimeString = (row, keys = ["timestamp", "time", "startTime", "createdAt"]) => {
  const raw = keys.map((key) => row?.[key]).find(Boolean);
  if (!raw) return "";

  if (typeof raw === "string") {
    const timeMatch = raw.match(/(?:^|[T,\s])(\d{1,2}):(\d{2})(?::\d{2})?/);
    if (timeMatch) {
      return `${timeMatch[1].padStart(2, "0")}:${timeMatch[2]}`;
    }
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";

  return parsed.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const getHourLabel = (row, keys) => {
  const time = getTimeString(row, keys);
  if (!time) return "";
  const hour = time.split(":")[0]?.padStart(2, "0");
  return hour ? `${hour}:00` : "";
};

const getDateLabel = (row, keys = ["timestamp", "time", "startTime", "createdAt"]) => {
  const raw = keys.map((key) => row?.[key]).find(Boolean);
  if (!raw) return "";

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    });
  }

  if (typeof raw === "string") {
    const dateMatch = raw.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) return `${dateMatch[3]} ${dateMatch[2]}`;
  }

  return "";
};

const getDateSortValue = (row, keys = ["timestamp", "time", "startTime", "createdAt"]) => {
  const raw = keys.map((key) => row?.[key]).find(Boolean);
  if (!raw) return "";

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  if (typeof raw === "string") {
    const dateMatch = raw.match(/(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) return `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
  }

  return "";
};

const getTimeBucket = (row, keys, includeDate = false) => {
  const hour = getHourLabel(row, keys);
  if (!hour) return null;

  if (!includeDate) {
    return { label: hour, sortKey: hour };
  }

  const date = getDateLabel(row, keys);
  const dateSortValue = getDateSortValue(row, keys);
  const label = date || hour;
  return { label, sortKey: dateSortValue || label };
};

const parseTimeToMinutes = (value) => {
  if (!value) return 0;
  if (typeof value === "number") return value;

  const text = String(value).trim();
  if (/^\d{1,2}:\d{2}/.test(text)) {
    const [hours, minutes] = text.split(":").map(Number);
    return (hours || 0) * 60 + (minutes || 0);
  }

  const hours = Number(text.match(/(\d+(?:\.\d+)?)\s*h/)?.[1] || 0);
  const minutes = Number(text.match(/(\d+(?:\.\d+)?)\s*m/)?.[1] || 0);
  const seconds = Number(text.match(/(\d+(?:\.\d+)?)\s*s/)?.[1] || 0);
  return hours * 60 + minutes + seconds / 60;
};

const getTripDurationHours = (row) => {
  const explicitDuration =
    row?.durationHours ||
    row?.durationInHours ||
    row?.totalHours ||
    row?.duration;
  const explicitMinutes = parseTimeToMinutes(explicitDuration);
  if (explicitMinutes > 0) return explicitMinutes / 60;

  const startMinutes = parseTimeToMinutes(row?.startTime);
  let endMinutes = parseTimeToMinutes(row?.endTime);
  if (!startMinutes || !endMinutes) return 0;
  if (endMinutes < startMinutes) endMinutes += 24 * 60;
  return Math.max(endMinutes - startMinutes, 0) / 60;
};

const groupByHour = (rows, getValue, keys, includeDate = false) => {
  const grouped = new Map();

  rows.forEach((row) => {
    const bucket = getTimeBucket(row, keys, includeDate);
    if (!bucket) return;
    const existing = grouped.get(bucket.label) || {
      time: bucket.label,
      sortKey: bucket.sortKey,
      value: 0,
    };
    existing.value += getValue(row);
    grouped.set(bucket.label, existing);
  });

  return Array.from(grouped.values())
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .map(({ time, value }) => ({ time, value }));
};

const mapMaxSpeedByTime = (overspeedRows, includeDate = false) => {
  const grouped = new Map();

  overspeedRows.forEach((row) => {
    const bucket = getTimeBucket(row, ["timestamp", "time", "createdAt"], includeDate);
    const speed = Number(row?.speed || row?.maxSpeed || 0);
    if (!bucket || speed <= 0) return;
    const current = grouped.get(bucket.label) || {
      time: bucket.label,
      sortKey: bucket.sortKey,
      speed: 0,
    };
    current.speed = Math.max(current.speed, speed);
    grouped.set(bucket.label, current);
  });

  return Array.from(grouped.values())
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .map(({ time, speed }) => ({ time, speed }));
};

const mapOverspeedIncidentsByTime = (overspeedRows, includeDate = false) =>
  groupByHour(overspeedRows, () => 1, ["timestamp", "time", "createdAt"], includeDate).map(
    (item) => ({ time: item.time, count: item.value }),
  );

const mapRuntimeByTime = (tripRows, runtimeRows, includeDate = false) => {
  const tripRuntime = groupByHour(
    tripRows,
    getTripDurationHours,
    ["startTime", "timestamp", "createdAt"],
    includeDate,
  ).map((item) => ({ time: item.time, runtime: Number(item.value.toFixed(2)) }));

  if (tripRuntime.length) return tripRuntime;

  return groupByHour(
    runtimeRows,
    (row) => Number(row?.totalMovingHours || row?.movingHours || 0),
    ["timestamp", "time", "createdAt"],
    includeDate,
  ).map((item) => ({ time: item.time, runtime: Number(item.value.toFixed(2)) }));
};

const mapDistanceByTime = (runtimeRows, tripRows, includeDate = false) => {
  const runtimeDistance = groupByHour(
    runtimeRows,
    (row) => Number(row?.noOfKmsCovered || row?.distance || row?.distanceCovered || 0),
    ["timestamp", "time", "createdAt"],
    includeDate,
  );

  if (runtimeDistance.length) {
    let cumulativeDistance = 0;
    return runtimeDistance.map((item) => {
      cumulativeDistance += item.value;
      return {
        time: item.time,
        distance: Number(cumulativeDistance.toFixed(2)),
      };
    });
  }

  const tripDistance = groupByHour(
    tripRows,
    (row) => Number(row?.distance || row?.distanceCovered || row?.kmsCovered || 0),
    ["startTime", "timestamp", "createdAt"],
    includeDate,
  );

  let cumulativeDistance = 0;
  return tripDistance.map((item) => {
    cumulativeDistance += item.value;
    return {
      time: item.time,
      distance: Number(cumulativeDistance.toFixed(2)),
    };
  });
};

const scaleDistanceToTotal = (distanceByTime, totalDistance) => {
  if (!distanceByTime.length || !totalDistance) return distanceByTime;

  const finalPlottedDistance =
    distanceByTime[distanceByTime.length - 1]?.distance || 0;

  if (finalPlottedDistance <= 0) {
    const stepDistance = totalDistance / distanceByTime.length;
    return distanceByTime.map((item, index) => ({
      ...item,
      distance: Number((stepDistance * (index + 1)).toFixed(2)),
    }));
  }

  if (finalPlottedDistance >= totalDistance * 0.95) {
    return distanceByTime;
  }

  const scale = totalDistance / finalPlottedDistance;
  return distanceByTime.map((item) => ({
    ...item,
    distance: Number((item.distance * scale).toFixed(2)),
  }));
};

const mapTripStatus = (tripRows) => {
  const counts = {
    Delayed: 0,
    Missed: 0,
    "On Time": 0,
  };

  tripRows.forEach((row) => {
    const status = String(
      row?.status ||
        row?.tripStatus ||
        row?.scheduleStatus ||
        row?.completionStatus ||
        "",
    )
      .trim()
      .toLowerCase();

    if (
      status === "completed" ||
      status === "complete" ||
      status === "on time" ||
      status === "ontime" ||
      status === "on-time"
    ) {
      counts["On Time"] += 1;
    } else if (
      status.includes("miss") ||
      status === "cancelled" ||
      status === "canceled"
    ) {
      counts.Missed += 1;
    } else if (
      status.includes("delay") ||
      status.includes("late") ||
      status.includes("not completed") ||
      status.includes("over")
    ) {
      counts.Delayed += 1;
    }
  });

  return Object.entries(counts)
    .filter(([, value]) => value > 0)
    .map(([name, value]) => ({
      name,
      value,
      color: tripStatusColors[name],
    }));
};

const analyticsHeaderSx = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "stretch", sm: "center" },
  justifyContent: "flex-end",
  gap: 1,
  mb: 2,
};

const selectSx = {
  borderRadius: "12px",
  minWidth: 180,
  height: 43,
  fontSize: "0.95rem",
  color: "#1F2937",
  backgroundColor: "#FFFFFF",
  border: "1px solid #183059",
  boxSizing: "border-box",
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& fieldset": {
    border: "none",
  },
  ".MuiSelect-select": {
    paddingTop: "12px",
    paddingRight: "24px",
    paddingBottom: "12px",
    paddingLeft: "24px",
    height: 43,
    display: "flex",
    alignItems: "center",
  },
};

const translateLegendValue = (value, t) =>
  typeof value === "string" ? t(value) : value;

const formatRuntimeAxisTick = (value) => {
  if (!Number.isFinite(value)) return "0h";
  if (value >= 1) return `${Math.round(value)}h`;
  const minutes = Math.round(value * 60);
  return `${minutes}m`;
};

const formatChartDateLabel = (value) => {
  const date = value instanceof Date ? value : new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
};

const getDistanceTitleRange = (dateOption, customDateRange) => {
  if (dateOption === "last 7 days") {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 6);
    return `${formatChartDateLabel(start)} - ${formatChartDateLabel(end)}`;
  }

  if (dateOption === "custom") {
    const start = formatChartDateLabel(customDateRange?.startDate);
    const end = formatChartDateLabel(customDateRange?.endDate);
    return start && end ? `${start} - ${end}` : "";
  }

  return "";
};

const mapHaltsByTime = (haltRows, includeDate = false) => {
  const grouped = new Map();

  haltRows.forEach((row) => {
    const bucket = getTimeBucket(
      row,
      ["startTime", "timestamp", "createdAt"],
      includeDate,
    );
    if (!bucket) return;

    const current = grouped.get(bucket.label) || {
      time: bucket.label,
      sortKey: bucket.sortKey,
      dead: 0,
      inactive: 0,
    };
    current.inactive += 1;
    grouped.set(bucket.label, current);
  });

  return Array.from(grouped.values())
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .map(({ time, dead, inactive }) => ({ time, dead, inactive }));
};

const sumByKey = (rows, key) =>
  rows.reduce((sum, row) => sum + Number(row?.[key] || 0), 0);

const EMPTY_ANALYTICS_MESSAGE = "No data available for the selected period";
const ANALYTICS_LOAD_ERROR = "Failed to load analytics";
const EMPTY_REPORT_ERRORS = {
  overspeed: "",
  runtime: "",
  trip: "",
  halt: "",
};

const AnalyticsCard = ({ title, noData, error, children, progress, t }) => (
  <Box sx={{ ...chartCardSx, position: "relative" }}>
    <ChunkLoadingIndicator progress={progress} t={t} />
    <Typography
      sx={{
        color: "#0F2D5C",
        fontWeight: 700,
        fontSize: 16,
        textAlign: "center",
      }}
    >
      {title}
    </Typography>
    <Box sx={chartFrameSx}>
      {error ? (
        <Box
          sx={{
            height: "100%",
            minHeight: 220,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#B42318",
            textAlign: "center",
            px: 2,
            fontWeight: 600,
          }}
        >
          {t(error)}
        </Box>
      ) : noData ? (
        <Box
          sx={{
            height: "100%",
            minHeight: 220,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#647490",
            textAlign: "center",
            px: 2,
          }}
        >
          {t(EMPTY_ANALYTICS_MESSAGE)}
        </Box>
      ) : (
        children
      )}
    </Box>
  </Box>
);

const ChunkLoadingIndicator = ({ progress, t }) => {
  if (!progress || progress.percentComplete >= 100) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 2,
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          border: "1px solid #D7E3F0",
          borderRadius: "999px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          color: "#5F6B7A",
          fontSize: 12,
          fontWeight: 600,
          px: 1.25,
          py: 0.5,
        }}
      >
        <CircularProgress size={14} thickness={5} />
        <span>{progress.percentComplete}%</span>
      </Box>
    </Box>
  );
};

const AnalyticsCardSkeleton = () => (
  <Box sx={chartCardSx}>
    <Skeleton
      variant="text"
      width="44%"
      height={28}
      sx={{ mx: "auto", bgcolor: "#E7EEF7" }}
    />
    <Box sx={chartFrameSx}>
      <Box
        sx={{
          height: "100%",
          minHeight: 220,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: 1.5,
          px: 1,
          py: 2,
        }}
      >
        <Skeleton
          variant="rectangular"
          height={22}
          width="25%"
          sx={{ alignSelf: "flex-end", bgcolor: "#E7EEF7", borderRadius: 1 }}
        />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "flex-end",
            gap: 1,
          }}
        >
          {[42, 78, 56, 88, 64, 72, 50, 82].map((height, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              height={`${height}%`}
              sx={{
                flex: 1,
                bgcolor: "#E7EEF7",
                borderRadius: "6px 6px 0 0",
              }}
            />
          ))}
        </Box>
        <Skeleton
          variant="text"
          width="38%"
          height={20}
          sx={{ mx: "auto", bgcolor: "#E7EEF7" }}
        />
      </Box>
    </Box>
  </Box>
);

export default function AnalyticsTable({
  vehicleId,
  deviceId,
  vehicle,
  t = (value) => value,
}) {
  const [reports, setReports] = useState({
    overspeed: null,
    runtime: null,
    trip: null,
    halt: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reportErrors, setReportErrors] = useState(EMPTY_REPORT_ERRORS);
  const [dateError, setDateError] = useState("");
  const [dateOption, setDateOption] = useState("today");
  const [chunkProgress, setChunkProgress] = useState(null);
  const [customDateRange, setCustomDateRange] = useState({
    startDate: formatISODateIST(new Date()),
    endDate: formatISODateIST(new Date()),
  });
  const [draftCustomDateRange, setDraftCustomDateRange] = useState({
    startDate: formatISODateIST(new Date()),
    endDate: formatISODateIST(new Date()),
  });

  const userDateFormat = getUserDateFormat();
  const dateInputFormat = convertToDateFnsFormat(userDateFormat);
  const parseDate = (d) => {
    if (!d) return null;
    if (d instanceof Date) return isValid(d) ? d : null;
    const parsed = new Date(`${d}T00:00:00`);
    return isValid(parsed) ? parsed : null;
  };

  const selectedDeviceId =
    deviceId || vehicle?.device_id || vehicle?.deviceId || vehicleId;
  const isWideChartRange =
    dateOption === "last 7 days" || dateOption === "custom";
  const distanceTitleRange = getDistanceTitleRange(dateOption, customDateRange);
  const sensorCharts = useAnalyticsSensorCharts({
    vehicleId: vehicleId || vehicle?.id,
    organizationId: getOrganizationId(),
    dateOption,
    customDateRange,
  });

  const renderXAxis = (data, label) => (
    <XAxis
      {...getXAxisProps(data)}
      label={{ value: label, position: "bottom", fontSize: 12 }}
    />
  );

  useEffect(() => {
    const fetchAnalytics = async () => {
      const orgId = getOrganizationId();
      if (!selectedDeviceId || !orgId) {
        setReports({ overspeed: null, runtime: null, trip: null, halt: null });
        setChunkProgress(null);
        return;
      }

      const baseParams = {
        orgId,
        siteId: null,
        category: "vehicles",
        selectedIds: [selectedDeviceId],
        selectAll: false,
      };

      const rangeError = validateAnalyticsDateRange(dateOption, customDateRange);
      if (rangeError) {
        setDateError(rangeError);
        setError(rangeError);
        setLoading(false);
        setReports({ overspeed: null, runtime: null, trip: null, halt: null });
        setReportErrors(EMPTY_REPORT_ERRORS);
        setChunkProgress(null);
        return;
      }

      setDateError("");
      setLoading(true);
      setError("");
      setReportErrors(EMPTY_REPORT_ERRORS);
      setReports({ overspeed: null, runtime: null, trip: null, halt: null });

      try {
        const chunks = createAnalyticsDateChunks(dateOption, customDateRange);
        setChunkProgress(
          chunks.length > 1
            ? { current: 0, total: chunks.length, percentComplete: 0 }
            : null,
        );
        const accumulated = {
          overspeed: [],
          runtime: [],
          trip: [],
          halt: [],
        };
        const failed = {
          overspeed: false,
          runtime: false,
          trip: false,
          halt: false,
        };

        let lastBackendError = "";
        const recordReport = (key, result) => {
          if (result.status === "fulfilled") {
            accumulated[key].push(result.value);
            return;
          }
          failed[key] = true;
          const msg =
            result.reason?.response?.data?.message ||
            result.reason?.response?.data?.error;
          if (msg && !lastBackendError) {
            lastBackendError = msg;
          }
          console.error(`Failed to fetch ${key} report:`, result.reason);
        };

        for (const [chunkIndex, chunk] of chunks.entries()) {
          const [overspeedRes, runtimeRes, tripRes, haltRes] =
            await Promise.allSettled([
              reportsApi.getOverspeedReport({ ...baseParams, ...chunk }),
              reportsApi.getRuntimeReport({ ...baseParams, ...chunk }),
              reportsApi.getTripReport({ ...baseParams, ...chunk }),
              reportsApi.getHaltReport({ ...baseParams, ...chunk }),
            ]);

          recordReport("overspeed", overspeedRes);
          recordReport("runtime", runtimeRes);
          recordReport("trip", tripRes);
          recordReport("halt", haltRes);

          const everyReportFailed =
            failed.overspeed &&
            failed.runtime &&
            failed.trip &&
            failed.halt &&
            accumulated.overspeed.length === 0 &&
            accumulated.runtime.length === 0 &&
            accumulated.trip.length === 0 &&
            accumulated.halt.length === 0;

          if (!everyReportFailed) {
            setReports({
              overspeed: mergeReportResponses(accumulated.overspeed),
              runtime: mergeReportResponses(accumulated.runtime),
              trip: mergeReportResponses(accumulated.trip),
              halt: mergeReportResponses(accumulated.halt),
            });
            setReportErrors({
              overspeed:
                failed.overspeed && accumulated.overspeed.length === 0
                  ? ANALYTICS_LOAD_ERROR
                  : "",
              runtime:
                failed.runtime && accumulated.runtime.length === 0
                  ? ANALYTICS_LOAD_ERROR
                  : "",
              trip:
                failed.trip && accumulated.trip.length === 0
                  ? ANALYTICS_LOAD_ERROR
                  : "",
              halt:
                failed.halt && accumulated.halt.length === 0
                  ? ANALYTICS_LOAD_ERROR
                  : "",
            });
            setLoading(false);
          }
          if (chunks.length > 1) {
            setChunkProgress({
              current: chunkIndex + 1,
              total: chunks.length,
              percentComplete: Math.round(((chunkIndex + 1) / chunks.length) * 100),
            });
          }
        }

        const everyReportFailed =
          failed.overspeed &&
          failed.runtime &&
          failed.trip &&
          failed.halt &&
          accumulated.overspeed.length === 0 &&
          accumulated.runtime.length === 0 &&
          accumulated.trip.length === 0 &&
          accumulated.halt.length === 0;

        if (everyReportFailed) {
          setReports({ overspeed: null, runtime: null, trip: null, halt: null });
          setReportErrors(EMPTY_REPORT_ERRORS);
          const displayError = lastBackendError || ANALYTICS_LOAD_ERROR;
          setError(displayError);
          if (lastBackendError) {
            setDateError(lastBackendError);
          }
        }
      } catch (err) {
        console.error("Error fetching vehicle analytics:", err);
        const backendMessage =
          err?.response?.data?.message ||
          err?.response?.data?.error;
        setError(backendMessage || ANALYTICS_LOAD_ERROR);
        if (backendMessage) {
          setDateError(backendMessage);
        }
      } finally {
        setLoading(false);
        setChunkProgress(null);
      }
    };

    fetchAnalytics();
  }, [selectedDeviceId, dateOption, customDateRange.startDate, customDateRange.endDate]);

  const handleApplyCustomDateRange = () => {
    const rangeError = validateAnalyticsDateRange(
      "custom",
      draftCustomDateRange,
    );

    if (rangeError) {
      setDateError(rangeError);
      setError(rangeError);
      setLoading(false);
      setChunkProgress(null);
      setReports({ overspeed: null, runtime: null, trip: null, halt: null });
      setReportErrors(EMPTY_REPORT_ERRORS);
      return;
    }

    setDateError("");
    setCustomDateRange(draftCustomDateRange);
  };

  const analytics = useMemo(() => {
    const includeDateOnAxis = dateOption === "last 7 days" || dateOption === "custom";
    const overspeedRows = getRows(reports.overspeed);
    const runtimeRows = getRows(reports.runtime);
    const tripRows = getRows(reports.trip);
    const haltRows = flattenHaltRows(reports.halt);

    const maxSpeedByTime = mapMaxSpeedByTime(overspeedRows, includeDateOnAxis);
    const overspeedByTime = mapOverspeedIncidentsByTime(
      overspeedRows,
      includeDateOnAxis,
    );
    const runtimeByTime = mapRuntimeByTime(
      tripRows,
      runtimeRows,
      includeDateOnAxis,
    );
    const rawDistanceByTime = mapDistanceByTime(
      runtimeRows,
      tripRows,
      includeDateOnAxis,
    );
    const aggregateDistance =
      sumByKey(runtimeRows, "noOfKmsCovered") ||
      sumByKey(runtimeRows, "distance") ||
      sumByKey(runtimeRows, "distanceCovered") ||
      sumByKey(tripRows, "distance") ||
      sumByKey(tripRows, "distanceCovered") ||
      sumByKey(tripRows, "kmsCovered");
    const distanceByTime = scaleDistanceToTotal(
      rawDistanceByTime,
      aggregateDistance,
    );
    const tripStatus = mapTripStatus(tripRows);
    const haltsByTime = mapHaltsByTime(haltRows, includeDateOnAxis);

    const maxSpeed = maxSpeedByTime.length
      ? Math.max(...maxSpeedByTime.map((row) => row.speed))
      : 0;
    const totalRuntime = runtimeByTime.reduce(
      (sum, row) => sum + row.runtime,
      0,
    );
    const totalDistance =
      distanceByTime[distanceByTime.length - 1]?.distance ||
      aggregateDistance;
    const totalHalts = haltsByTime.reduce(
      (sum, row) => sum + row.dead + row.inactive,
      0,
    );

    return {
      maxSpeed,
      maxSpeedByTime,
      overspeedByTime,
      overspeedCount: overspeedRows.length,
      runtimeByTime,
      totalRuntime,
      distanceByTime,
      totalDistance,
      tripStatus,
      tripStatusTotal: tripStatus.reduce((sum, row) => sum + row.value, 0),
      haltsByTime,
      totalHalts,
    };
  }, [reports, dateOption]);

  if (!selectedDeviceId) {
    return (
      <Box sx={{ p: 3, textAlign: "center", color: "#647490" }}>
        {t("No vehicle selected")}
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={analyticsHeaderSx}>
        <FormControl
          size="small"
          sx={{
            minWidth: 180,
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            height: 43,
          }}
        >
          <Select
            id="vehicle-analytics-duration"
            value={dateOption}
            onChange={(event) => {
              setDateOption(event.target.value);
              setDateError("");
            }}
            input={
              <OutlinedInput
                notched={false}
                sx={{
                  borderRadius: "12px",
                  border: "none",
                  height: 43,
                  px: 0,
                  "& fieldset": { border: "none" },
                }}
              />
            }
            displayEmpty
            renderValue={(selected) => {
              switch (selected) {
                case "today":
                  return t("Today");
                case "yesterday":
                  return t("Yesterday");
                case "last 7 days":
                  return t("This Week");
                case "custom":
                  return t("Custom");
                default:
                  return t("Today");
              }
            }}
            sx={selectSx}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: "12px",
                  mt: 1,
                  minWidth: 180,
                },
              },
            }}
          >
            <MenuItem value="today">{t("Today")}</MenuItem>
            <MenuItem value="yesterday">{t("Yesterday")}</MenuItem>
            <MenuItem value="last 7 days">{t("This Week")}</MenuItem>
            <MenuItem value="custom">{t("Custom")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {dateOption === "custom" && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "flex-end",
              alignItems: { xs: "stretch", sm: "flex-start" },
              gap: 1,
              mb: 2,
            }}
          >
            <DatePicker
              label={t("Start Date")}
              value={parseDate(draftCustomDateRange.startDate)}
              maxDate={parseDate(draftCustomDateRange.endDate) || undefined}
              onChange={(newDate) => {
                const val = newDate && isValid(newDate) ? format(newDate, "yyyy-MM-dd") : "";
                setDraftCustomDateRange((prev) => ({
                  ...prev,
                  startDate: val,
                }));
                setDateError("");
              }}
              format={dateInputFormat}
              slotProps={{
                textField: {
                  size: "small",
                  error: !!dateError,
                  placeholder: userDateFormat,
                  InputLabelProps: { shrink: true },
                  sx: { backgroundColor: "#fff" },
                },
              }}
            />
            <DatePicker
              label={t("End Date")}
              value={parseDate(draftCustomDateRange.endDate)}
              minDate={parseDate(draftCustomDateRange.startDate) || undefined}
              onChange={(newDate) => {
                const val = newDate && isValid(newDate) ? format(newDate, "yyyy-MM-dd") : "";
                setDraftCustomDateRange((prev) => ({
                  ...prev,
                  endDate: val,
                }));
                setDateError("");
              }}
              format={dateInputFormat}
              slotProps={{
                textField: {
                  size: "small",
                  error: !!dateError,
                  helperText: dateError ? t(dateError) : " ",
                  placeholder: userDateFormat,
                  InputLabelProps: { shrink: true },
                  sx: { backgroundColor: "#fff" },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleApplyCustomDateRange}
              sx={{
                minWidth: 110,
                height: 40,
                backgroundColor: "#20A8E0",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#168FC2" },
              }}
            >
              {t("Apply")}
            </Button>
          </Box>
        </LocalizationProvider>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: isWideChartRange ? "1fr" : "repeat(2, minmax(0, 1fr))",
            lg: isWideChartRange ? "1fr" : "repeat(3, minmax(0, 1fr))",
          },
          gap: 3,
        }}
      >
        {loading ? (
          Array.from({ length: 6 }, (_, index) => (
            <AnalyticsCardSkeleton key={index} />
          ))
        ) : error ? (
          <Typography color="error" sx={{ p: 2, textAlign: "center", gridColumn: "1 / -1", fontWeight: 600 }}>
            {t(error)}
          </Typography>
        ) : (
          <>
            <AnalyticsCard
              title={`${t("Maximum Speed")}: ${analytics.maxSpeed} km/h`}
              error={
                reportErrors.overspeed && !analytics.maxSpeedByTime.length
                  ? reportErrors.overspeed
                  : ""
              }
              noData={!analytics.maxSpeedByTime.length}
              progress={chunkProgress}
              t={t}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.maxSpeedByTime} margin={{ top: 8, right: 12, left: 8, bottom: 28 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  {renderXAxis(analytics.maxSpeedByTime, t("Time"))}
                  <YAxis tick={axisStyle} label={{ value: t("Speed (km/h)"), angle: -90, position: "insideLeft", fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="speed" fill="#162F5E" radius={[8, 8, 0, 0]} maxBarSize={18} minPointSize={2} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </AnalyticsCard>

      <AnalyticsCard
        title={`${t("Overspeed Incidents")}: ${analytics.overspeedCount}`}
        error={
          reportErrors.overspeed && !analytics.overspeedByTime.length
            ? reportErrors.overspeed
            : ""
        }
        noData={!analytics.overspeedByTime.length}
        progress={chunkProgress}
        t={t}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={analytics.overspeedByTime} margin={{ top: 8, right: 12, left: 8, bottom: 28 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            {renderXAxis(analytics.overspeedByTime, t("Time"))}
            <YAxis tick={axisStyle} allowDecimals={false} label={{ value: t("Incidents"), angle: -90, position: "insideLeft", fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="count" fill="#F33A2F" radius={[8, 8, 0, 0]} maxBarSize={18} minPointSize={2} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </AnalyticsCard>

      <AnalyticsCard
        title={`${t("Total Runtime")}: ${analytics.totalRuntime.toFixed(2)} hrs`}
        error={
          !analytics.runtimeByTime.length &&
          (reportErrors.runtime || reportErrors.trip)
            ? ANALYTICS_LOAD_ERROR
            : ""
        }
        noData={!analytics.runtimeByTime.length}
        progress={chunkProgress}
        t={t}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={analytics.runtimeByTime} margin={{ top: 8, right: 12, left: 8, bottom: 28 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            {renderXAxis(analytics.runtimeByTime, t("Time"))}
            <YAxis
              tick={axisStyle}
              tickFormatter={formatRuntimeAxisTick}
              label={{ value: t("Runtime"), angle: -90, position: "insideLeft", fontSize: 12 }}
            />
            <Tooltip />
            <Bar dataKey="runtime" fill="#008B22" radius={[8, 8, 0, 0]} maxBarSize={18} minPointSize={2} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </AnalyticsCard>

      <AnalyticsCard
        title={`${t("Distance Covered")}${
          distanceTitleRange ? ` (${distanceTitleRange})` : ""
        }: ${analytics.totalDistance.toFixed(2)} km`}
        error={
          !analytics.distanceByTime.length &&
          (reportErrors.runtime || reportErrors.trip)
            ? ANALYTICS_LOAD_ERROR
            : ""
        }
        noData={!analytics.distanceByTime.length}
        progress={chunkProgress}
        t={t}
      >
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={analytics.distanceByTime} margin={{ top: 12, right: 16, left: 8, bottom: 28 }}>
            <CartesianGrid strokeDasharray="3 3" />
            {renderXAxis(analytics.distanceByTime, t("Time"))}
            <YAxis tick={axisStyle} label={{ value: t("Distance (km)"), angle: -90, position: "insideLeft", fontSize: 12 }} />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="distance"
              stroke="#22AEEA"
              strokeWidth={3}
              dot={analytics.distanceByTime.length > 18 ? false : { r: 5, strokeWidth: 3, fill: "#FFFFFF" }}
              activeDot={{ r: 5, strokeWidth: 2, fill: "#FFFFFF" }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </AnalyticsCard>

      <AnalyticsCard
        title={`${t("Trip Status")}: ${analytics.tripStatusTotal}`}
        error={
          reportErrors.trip && !analytics.tripStatus.length
            ? reportErrors.trip
            : ""
        }
        noData={!analytics.tripStatus.length}
        progress={chunkProgress}
        t={t}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={analytics.tripStatus}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="45%"
              outerRadius={82}
              label={({ value }) => value}
              isAnimationActive={false}
            >
              {analytics.tripStatus.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => [
                value,
                translateLegendValue(name, t),
              ]}
            />
            <Legend
              verticalAlign="bottom"
              iconType="square"
              formatter={(value) => translateLegendValue(value, t)}
            />
          </PieChart>
        </ResponsiveContainer>
      </AnalyticsCard>

            <AnalyticsCard
              title={`${t("Vehicle Halts")}: ${analytics.totalHalts}`}
              error={
                reportErrors.halt && !analytics.haltsByTime.length
                  ? reportErrors.halt
                  : ""
              }
              noData={!analytics.haltsByTime.length}
              progress={chunkProgress}
              t={t}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={analytics.haltsByTime} margin={{ top: 8, right: 12, left: 8, bottom: 28 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  {renderXAxis(analytics.haltsByTime, t("Time"))}
                  <YAxis tick={axisStyle} allowDecimals={false} label={{ value: t("Halts"), angle: -90, position: "insideLeft", fontSize: 12 }} />
                  <Tooltip />
                  <Legend verticalAlign="top" iconType="square" />
                  <Bar dataKey="dead" name={t("dead")} fill="#F33A2F" radius={[8, 8, 0, 0]} maxBarSize={16} minPointSize={2} isAnimationActive={false} />
                  <Bar dataKey="inactive" name={t("inactive")} fill="#FFE600" radius={[8, 8, 0, 0]} maxBarSize={16} minPointSize={2} isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
            </AnalyticsCard>

            {sensorCharts.map((chart) => (
              <AnalyticsCard
                key={chart.key}
                title={`${t(chart.label)} ${t("Over Time")}: ${chart.avg} ${t("avg")}`}
                noData={!chart.points.length}
                t={t}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chart.points} margin={{ top: 12, right: 16, left: 8, bottom: 28 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    {renderXAxis(chart.points, t("Time"))}
                    <YAxis
                      tick={axisStyle}
                      domain={chart.domain || ["auto", "auto"]}
                      label={{
                        value: t(chart.yLabel),
                        angle: -90,
                        position: "insideLeft",
                        fontSize: 12,
                      }}
                    />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={chart.color}
                      strokeWidth={3}
                      dot={
                        chart.points.length > 18
                          ? false
                          : { r: 5, strokeWidth: 3, fill: "#FFFFFF" }
                      }
                      activeDot={{ r: 5, strokeWidth: 2, fill: "#FFFFFF" }}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </AnalyticsCard>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
}
